import type { CSSProperties } from 'react';
import {
  Component,
  ComponentType,
  Position,
  Size,
  ResponsivePosition,
  PositioningStrategy,
  ResponsiveConstraints,
  ResponsiveConstraintValues,
  ResponsiveSize,
  BreakpointOverride,
  Viewport
} from '@/types';

export interface ContainerDimensions {
  width: number;
  height: number;
}

export interface TranslationOptions {
  viewport: Viewport;
  containerDimensions: ContainerDimensions;
  parentComponent?: Component;
  enableSnapping?: boolean;
  snapGridSize?: number;
}

export interface TranslatedStyles {
  position?: CSSProperties['position'];
  left?: CSSProperties['left'];
  top?: CSSProperties['top'];
  right?: CSSProperties['right'];
  bottom?: CSSProperties['bottom'];
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  minWidth?: CSSProperties['minWidth'];
  maxWidth?: CSSProperties['maxWidth'];
  minHeight?: CSSProperties['minHeight'];
  maxHeight?: CSSProperties['maxHeight'];
  aspectRatio?: CSSProperties['aspectRatio'];
  display?: CSSProperties['display'];
  flexDirection?: CSSProperties['flexDirection'];
  flexWrap?: CSSProperties['flexWrap'];
  justifyContent?: CSSProperties['justifyContent'];
  alignItems?: CSSProperties['alignItems'];
  alignSelf?: CSSProperties['alignSelf'];
  justifySelf?: CSSProperties['justifySelf'];
  gap?: CSSProperties['gap'];
  gridArea?: CSSProperties['gridArea'];
  gridColumn?: CSSProperties['gridColumn'];
  gridRow?: CSSProperties['gridRow'];
  order?: CSSProperties['order'];
  flexGrow?: CSSProperties['flexGrow'];
  flexShrink?: CSSProperties['flexShrink'];
  flexBasis?: CSSProperties['flexBasis'];
  gridTemplateColumns?: CSSProperties['gridTemplateColumns'];
  gridTemplateRows?: CSSProperties['gridTemplateRows'];
  gridTemplateAreas?: CSSProperties['gridTemplateAreas'];
  gridGap?: CSSProperties['gridGap'];
  gridAutoFlow?: CSSProperties['gridAutoFlow'];
}

interface NumericSize {
  width: number;
  height: number;
}

type ResponsiveBreakpoint = 'mobile' | 'tablet' | 'desktop';

const RESPONSIVE_BREAKPOINTS: readonly ResponsiveBreakpoint[] = ['mobile', 'tablet', 'desktop'];
const CONSTRAINT_SLOT_KEYS: readonly (ResponsiveBreakpoint | 'base')[] = ['base', ...RESPONSIVE_BREAKPOINTS];

const ALIGN_SELF_VALUES = new Set([
  'auto',
  'flex-start',
  'flex-end',
  'center',
  'baseline',
  'stretch',
]);

const JUSTIFY_SELF_VALUES = new Set([
  'auto',
  'start',
  'end',
  'center',
  'stretch',
]);

/**
 * Service for translating between absolute positioning (editor) and responsive positioning (viewer)
 */
export class ResponsivePositionTranslator {
  private static readonly chartComponentTypes = new Set<ComponentType | string>([
    ComponentType.CHART,
    ComponentType.BAR_CHART,
    ComponentType.LINE_CHART,
    ComponentType.PIE_CHART,
    ComponentType.SCATTER_CHART,
    ComponentType.AREA_CHART,
    ComponentType.HEATMAP_CHART,
    ComponentType.TREEMAP_CHART,
    ComponentType.GAUGE_CHART,
  ]);

  // Table components should also get full-width treatment in flex containers
  private static readonly tableComponentTypes = new Set<ComponentType | string>([
    ComponentType.TABLE,
    ComponentType.ADVANCED_TABLE,
  ]);
  /**
   * Get the current viewport breakpoint
   */
  private getViewportBreakpoint(viewport: Viewport): ResponsiveBreakpoint {
    switch (viewport) {
      case 'mobile':
        return 'mobile';
      case 'tablet':
        return 'tablet';
      case 'desktop':
      default:
        return 'desktop';
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private hasV2ConstraintSlots(constraints: Record<string, unknown>): boolean {
    return CONSTRAINT_SLOT_KEYS.some(key => this.isRecord(constraints[key]));
  }

  private isAlignSelf(value: unknown): value is NonNullable<ResponsiveConstraints['alignSelf']> {
    return typeof value === 'string' && ALIGN_SELF_VALUES.has(value);
  }

  private isJustifySelf(value: unknown): value is NonNullable<ResponsiveConstraints['justifySelf']> {
    return typeof value === 'string' && JUSTIFY_SELF_VALUES.has(value);
  }

  private mergeConstraintRecord(
    target: ResponsiveConstraintValues,
    source: unknown
  ): ResponsiveConstraintValues {
    if (!this.isRecord(source)) {
      return target;
    }

    const next: ResponsiveConstraintValues = { ...target };

    const pinToEdges = source.pinToEdges;
    if (this.isRecord(pinToEdges)) {
      const nextPins: NonNullable<ResponsiveConstraintValues['pinToEdges']> = {
        ...next.pinToEdges,
      };
      if (typeof pinToEdges.top === 'boolean') nextPins.top = pinToEdges.top;
      if (typeof pinToEdges.right === 'boolean') nextPins.right = pinToEdges.right;
      if (typeof pinToEdges.bottom === 'boolean') nextPins.bottom = pinToEdges.bottom;
      if (typeof pinToEdges.left === 'boolean') nextPins.left = pinToEdges.left;
      next.pinToEdges = nextPins;
    }

    const percentagePosition = source.percentagePosition;
    if (
      this.isRecord(percentagePosition) &&
      typeof percentagePosition.x === 'number' &&
      typeof percentagePosition.y === 'number'
    ) {
      next.percentagePosition = {
        x: percentagePosition.x,
        y: percentagePosition.y,
      };
    }

    if (typeof source.gridArea === 'string') next.gridArea = source.gridArea;
    if (typeof source.gridColumn === 'string') next.gridColumn = source.gridColumn;
    if (typeof source.gridRow === 'string') next.gridRow = source.gridRow;
    if (typeof source.flexOrder === 'number') next.flexOrder = source.flexOrder;
    if (typeof source.flexGrow === 'number') next.flexGrow = source.flexGrow;
    if (typeof source.flexShrink === 'number') next.flexShrink = source.flexShrink;
    if (typeof source.flexBasis === 'string') next.flexBasis = source.flexBasis;
    if (this.isAlignSelf(source.alignSelf)) next.alignSelf = source.alignSelf;
    if (this.isJustifySelf(source.justifySelf)) next.justifySelf = source.justifySelf;

    return next;
  }

  private normalizeConstraints(
    constraints: unknown,
    breakpoint: ResponsiveBreakpoint
  ): ResponsiveConstraints | undefined {
    if (!this.isRecord(constraints)) {
      return undefined;
    }

    const normalized: ResponsiveConstraintValues = this.hasV2ConstraintSlots(constraints)
      ? [constraints.base, constraints[breakpoint]].reduce<ResponsiveConstraintValues>(
        (current, source) => this.mergeConstraintRecord(current, source),
        {}
      )
      : this.mergeConstraintRecord({}, constraints);

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private toNumericDimension(
    value: string | number | undefined,
    fallback: number
  ): number {
    return typeof value === 'number' ? value : fallback;
  }

  /**
   * Apply breakpoint-specific overrides
   */
  private applyBreakpointOverrides(
    basePosition: Position,
    baseSize: Size,
    responsivePosition: ResponsivePosition | undefined,
    viewport: Viewport
  ): { position: Position; size: Size | ResponsiveSize; constraints?: ResponsiveConstraints } {
    // Handle missing responsivePosition
    if (!responsivePosition) {
      return {
        position: basePosition,
        size: baseSize,
        constraints: undefined
      };
    }

    const breakpoint = this.getViewportBreakpoint(viewport);
    const override = responsivePosition.breakpoints?.[breakpoint];
    const baseConstraints = this.normalizeConstraints(
      responsivePosition.constraints,
      breakpoint
    );

    if (!override) {
      return {
        position: basePosition,
        size: baseSize,
        constraints: baseConstraints
      };
    }

    // If override.size exists, use ResponsiveSize, otherwise use base Size
    const overriddenSize: Size | ResponsiveSize = override.size ? {
      ...baseSize,
      ...override.size
    } : baseSize;
    const overrideConstraints = this.normalizeConstraints(override.constraints, breakpoint);
    const mergedConstraints = baseConstraints || overrideConstraints
      ? {
        ...baseConstraints,
        ...overrideConstraints
      }
      : undefined;

    return {
      position: {
        ...basePosition,
        ...override.position
      },
      size: overriddenSize,
      constraints: mergedConstraints
    };
  }

  /**
   * Convert absolute position to percentage-based position
   */
  private absoluteToPercentage(
    position: Position,
    size: NumericSize,
    container: ContainerDimensions
  ): { left: string; top: string; width: string; height: string } {
    const safeWidth = container.width > 0 ? container.width : 1;
    const safeHeight = container.height > 0 ? container.height : 1;

    return {
      left: `${(position.x / safeWidth) * 100}%`,
      top: `${(position.y / safeHeight) * 100}%`,
      width: size.width === 0 ? 'auto' : `${(size.width / safeWidth) * 100}%`,
      height: size.height === 0 ? 'auto' : `${(size.height / safeHeight) * 100}%`
    };
  }

  /**
   * Apply responsive constraints to generate CSS styles
   */
  private applyConstraints(
    constraints: ResponsiveConstraints,
    position: Position,
    size: NumericSize,
    container: ContainerDimensions
  ): TranslatedStyles {
    const styles: TranslatedStyles = {};

    // Handle edge pinning
    if (constraints.pinToEdges) {
      const { top, right, bottom, left } = constraints.pinToEdges;

      if (top && bottom) {
        styles.top = position.y;
        styles.bottom = container.height - (position.y + size.height);
        styles.height = 'auto';
      } else if (top) {
        styles.top = position.y;
        styles.height = size.height;
      } else if (bottom) {
        styles.bottom = container.height - (position.y + size.height);
        styles.height = size.height;
      }

      if (left && right) {
        styles.left = position.x;
        styles.right = container.width - (position.x + size.width);
        styles.width = 'auto';
      } else if (left) {
        styles.left = position.x;
        styles.width = size.width;
      } else if (right) {
        styles.right = container.width - (position.x + size.width);
        styles.width = size.width;
      }
    }

    // Handle percentage positioning
    if (constraints.percentagePosition) {
      styles.left = `${constraints.percentagePosition.x}%`;
      styles.top = `${constraints.percentagePosition.y}%`;
    }

    // Handle flexbox properties
    if (constraints.flexOrder !== undefined) {
      styles.order = constraints.flexOrder;
    }
    if (constraints.flexGrow !== undefined) {
      styles.flexGrow = constraints.flexGrow;
    }
    if (constraints.flexShrink !== undefined) {
      styles.flexShrink = constraints.flexShrink;
    }
    if (constraints.flexBasis) {
      styles.flexBasis = constraints.flexBasis;
    }
    if (constraints.alignSelf) {
      styles.alignSelf = constraints.alignSelf;
    }

    // Handle grid properties
    if (constraints.gridArea) {
      styles.gridArea = constraints.gridArea;
    }
    if (constraints.gridColumn) {
      styles.gridColumn = constraints.gridColumn;
    }
    if (constraints.gridRow) {
      styles.gridRow = constraints.gridRow;
    }
    if (constraints.justifySelf) {
      styles.justifySelf = constraints.justifySelf;
    }

    return styles;
  }

  /**
   * Apply responsive size configuration
   */
  private applyResponsiveSize(responsiveSize: ResponsiveSize): TranslatedStyles {
    const styles: TranslatedStyles = {};

    if (responsiveSize.width !== undefined) {
      styles.width = responsiveSize.width;
    }
    if (responsiveSize.height !== undefined) {
      styles.height = responsiveSize.height;
    }
    if (responsiveSize.minWidth !== undefined) {
      styles.minWidth = responsiveSize.minWidth;
    }
    if (responsiveSize.maxWidth !== undefined) {
      styles.maxWidth = responsiveSize.maxWidth;
    }
    if (responsiveSize.minHeight !== undefined) {
      styles.minHeight = responsiveSize.minHeight;
    }
    if (responsiveSize.maxHeight !== undefined) {
      styles.maxHeight = responsiveSize.maxHeight;
    }
    if (responsiveSize.aspectRatio !== undefined) {
      styles.aspectRatio = String(responsiveSize.aspectRatio);
    }

    return styles;
  }

  /**
   * Translate component to responsive styles for viewer mode
   */
  public translateToResponsive(
    component: Component,
    options: TranslationOptions
  ): TranslatedStyles {
    // Get responsivePosition, or generate default if missing
    const responsivePosition = component.responsivePosition ||
      this.generateDefaultResponsivePosition(component.type);

    // Apply breakpoint overrides
    const { position, size, constraints } = this.applyBreakpointOverrides(
      component.position || { x: 0, y: 0, z: 0 },
      component.size || { width: 100, height: 100 },
      responsivePosition,
      options.viewport
    );

    // Start with base styles based on strategy
    let styles: TranslatedStyles = {};

    // Check if size is ResponsiveSize (has string values) or regular Size (numeric)
    const isResponsiveSize = (s: Size | ResponsiveSize): s is ResponsiveSize => {
      return typeof s.width === 'string' || typeof s.height === 'string';
    };

    // Convert size to numeric values for calculations if needed
    const numericSize: NumericSize = {
      width: this.toNumericDimension(
        size.width,
        this.toNumericDimension(component.size?.width, 100)
      ),
      height: this.toNumericDimension(
        size.height,
        this.toNumericDimension(component.size?.height, 100)
      ),
    };

    switch (responsivePosition.strategy) {
      case 'absolute':
        // Use absolute positioning with optional percentage conversion
        if (constraints?.percentagePosition) {
          styles = {
            position: 'absolute',
            left: `${constraints.percentagePosition.x}%`,
            top: `${constraints.percentagePosition.y}%`,
            width: isResponsiveSize(size) ? size.width : numericSize.width,
            height: isResponsiveSize(size) ? size.height : numericSize.height
          };
        } else {
          const percentages = this.absoluteToPercentage(position, numericSize, options.containerDimensions);
          // For charts, preserve pixel height instead of using percentage
          if (ResponsivePositionTranslator.chartComponentTypes.has(component.type)) {
            styles = {
              position: 'absolute',
              left: percentages.left,
              top: percentages.top,
              width: percentages.width, // Will be overridden to 100% later
              height: numericSize.height // Keep pixel height for ResizeObserver
            };
          } else {
            styles = {
              position: 'absolute',
              ...percentages
            };
          }
        }
        break;

      case 'relative':
        // For relative positioning, respect responsive size if set
        if (responsivePosition.responsiveSize) {
          styles = {
            position: 'relative',
            width: responsivePosition.responsiveSize.width || '100%',
            height: responsivePosition.responsiveSize.height || 'auto'
          };
        } else {
          styles = {
            position: 'relative',
            width: isResponsiveSize(size) ? (size.width || '100%') : '100%',
            height: isResponsiveSize(size) ? (size.height || 'auto') : 'auto'
          };
        }
        break;

      case 'flex':
        // Component is a flex item
        // Check if responsiveSize specifically has width/height
        const responsiveSizeValues = responsivePosition.responsiveSize;
        if (responsiveSizeValues) {
          styles = {
            position: 'relative',
            width: responsiveSizeValues.width || 'auto',
            height: responsiveSizeValues.height || 'auto',
            minHeight: responsiveSizeValues.minHeight || undefined
          };
        } else {
          styles = {
            position: 'relative',
            width: '100%', // Default to full width for flex containers
            height: 'auto'
          };
        }
        break;

      case 'grid':
        // Component is a grid item
        styles = {
          position: 'relative',
          width: '100%',
          height: '100%'
        };
        break;

      case 'auto':
        // Auto layout - let the container decide
        styles = {
          position: 'relative',
          width: 'auto',
          height: 'auto'
        };
        break;
    }

    // Apply constraints if they exist
    if (constraints) {
      const constraintStyles = this.applyConstraints(
        constraints,
        position,
        numericSize,
        options.containerDimensions
      );
      styles = { ...styles, ...constraintStyles };
    }

    // Apply responsive size if defined
    if (responsivePosition.responsiveSize) {
      const sizeStyles = this.applyResponsiveSize(responsivePosition.responsiveSize);
      styles = { ...styles, ...sizeStyles };
    }

    // Apply breakpoint-specific styles if they exist
    const breakpoint = this.getViewportBreakpoint(options.viewport);
    const breakpointOverride = responsivePosition.breakpoints?.[breakpoint];
    if (breakpointOverride?.style) {
      // Apply any additional styles from the breakpoint (including minWidth, maxWidth, etc.)
      styles = { ...styles, ...breakpointOverride.style };
    }

    // Ensure container components maintain responsive sizing defaults
    const isContainerComponent =
      component.type === ComponentType.FLEX_CONTAINER ||
      component.type === ComponentType.CONTAINER;
    const isRootContainer = isContainerComponent && !component.parentId;

    if (isRootContainer) {
      if (!styles.width || styles.width === 'auto') {
        styles.width = '100%';
      }
      if (!styles.maxWidth) {
        styles.maxWidth = '100%';
      }
      styles.position = 'relative';
      styles.left = undefined;
      styles.top = undefined;
      styles.right = undefined;
      styles.bottom = undefined;
    }

    // For ALL flex container children, preserve flex properties from the editor
    // This ensures consistent layout between editor and viewer modes
    if (component.parentId && !isContainerComponent) {
      // Check if flex properties exist in constraints (already applied above)
      const hasFlexFromConstraints = constraints?.flexGrow !== undefined ||
                                     constraints?.flexShrink !== undefined ||
                                     constraints?.flexBasis !== undefined;

      // If not from constraints, check component properties
      const flexValue = component.properties?.flex;
      if (!hasFlexFromConstraints && typeof flexValue === 'string') {
        const flexParts = flexValue.split(' ');
        if (flexParts.length === 3) {
          styles.flexGrow = parseInt(flexParts[0], 10) || 0;
          styles.flexShrink = parseInt(flexParts[1], 10) || 1;
          styles.flexBasis = flexParts[2];
        }
      }
    }

    // Ensure chart and table components expand to available width similar to editor canvas
    const isChartComponent = ResponsivePositionTranslator.chartComponentTypes.has(component.type);
    const isTableComponent = ResponsivePositionTranslator.tableComponentTypes.has(component.type);

    if (isChartComponent || isTableComponent) {
      if (!styles.width || styles.width === 'auto') {
        styles.width = '100%';
      }
      if (!styles.maxWidth) {
        styles.maxWidth = '100%';
      }

      if (component.parentId) {
        styles.position = 'relative';
        styles.left = undefined;
        styles.top = undefined;
        styles.right = undefined;
        styles.bottom = undefined;

        // Default flex behavior for charts/tables in containers if no flex was set already
        // This matches the logic in FlexContainer.tsx lines 684-686
        if (styles.flexGrow === undefined && styles.flexShrink === undefined && styles.flexBasis === undefined) {
          styles.flexGrow = 1;
          styles.flexShrink = 1;
          styles.flexBasis = '100%';
        }
      }

      // For charts, preserve the pixel height set in the absolute strategy case
      // Don't override it with 'auto' or minHeight
      if (!isChartComponent) {
        if (!styles.minHeight && component.size?.height) {
          styles.minHeight = component.size.height;
        }
        if (!styles.height) {
          styles.height = 'auto';
        }
      }
    }

    // Add container layout properties if this is a container
    if (responsivePosition.layoutMode && responsivePosition.layoutMode !== 'none') {
      if (responsivePosition.layoutMode === 'flex') {
        styles.display = 'flex';
        if (responsivePosition.flexDirection) styles.flexDirection = responsivePosition.flexDirection;
        if (responsivePosition.flexWrap) styles.flexWrap = responsivePosition.flexWrap;
        if (responsivePosition.justifyContent) styles.justifyContent = responsivePosition.justifyContent;
        if (responsivePosition.alignItems) styles.alignItems = responsivePosition.alignItems;
        if (responsivePosition.gap) styles.gap = responsivePosition.gap;
      } else if (responsivePosition.layoutMode === 'grid') {
        styles.display = 'grid';
        if (responsivePosition.gridTemplateColumns) styles.gridTemplateColumns = responsivePosition.gridTemplateColumns;
        if (responsivePosition.gridTemplateRows) styles.gridTemplateRows = responsivePosition.gridTemplateRows;
        if (responsivePosition.gridTemplateAreas) styles.gridTemplateAreas = responsivePosition.gridTemplateAreas;
        if (responsivePosition.gridGap) styles.gridGap = responsivePosition.gridGap;
        if (responsivePosition.gridAutoFlow) styles.gridAutoFlow = responsivePosition.gridAutoFlow;
      }
    }

    // Final adjustment for charts: ensure proper width/height for ResizeObserver measurement
    if (ResponsivePositionTranslator.chartComponentTypes.has(component.type)) {
      // Charts always use 100% width (set above in chart/table handling)
      // Height is preserved as pixel value from editor (not percentage) so ResizeObserver can measure it
      // The wrapper gets explicit pixel height, charts fill the wrapper using ResizeObserver
    }

    return styles;
  }

  /**
   * Check if a component should be hidden at current viewport
   */
  public isHiddenAtViewport(component: Component, viewport: Viewport): boolean {
    const breakpoint = this.getViewportBreakpoint(viewport);
    const override = component.responsivePosition?.breakpoints?.[breakpoint];

    return override?.hidden ?? component.hidden ?? false;
  }

  /**
   * Generate default responsive position for a component type
   */
  public generateDefaultResponsivePosition(componentType: string): ResponsivePosition {
    switch (componentType) {
      case 'container':
      case 'flex-container':
        // Containers use relative positioning with full width for viewer mode
        // This ensures they expand to fill available space instead of preserving editor pixel dimensions
        return {
          strategy: 'relative',
          layoutMode: 'none',
          responsiveSize: {
            width: '100%',
            minHeight: 100
          }
        };

      case 'text':
      case 'button':
      case 'input':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: 'auto',
            height: 'auto'
          }
        };

      case 'image':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '100%',
            height: 'auto',
            aspectRatio: 16/9
          }
        };

      case 'chart':
      case 'bar-chart':
      case 'line-chart':
      case 'pie-chart':
      case 'scatter-chart':
      case 'area-chart':
      case 'heatmap-chart':
      case 'treemap-chart':
      case 'gauge-chart':
        return {
          strategy: 'absolute', // Use absolute to preserve editor dimensions as percentage
          layoutMode: 'none',
          responsiveSize: {
            width: '100%', // Override to always use full width
            minHeight: 240
          }
        };

      case 'table':
      case 'advanced-table':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '100%',
            height: 'auto',
            minHeight: 200
          }
        };

      case 'file-storage':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '100%',
            height: 'auto',
            minHeight: 200
          }
        };

      case 'radial-dial':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '200px',
            height: '200px',
          }
        };

      case 'assignment-widget':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '100%',
            height: 'auto',
          }
        };

      case 'horizontal-timeline':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '100%',
            height: 'auto',
            minHeight: 120
          }
        };

      case 'checklist':
        return {
          strategy: 'relative',
          responsiveSize: {
            width: '100%',
            height: 'auto',
            minHeight: 200
          }
        };

      default:
        return {
          strategy: 'absolute'
        };
    }
  }
}

// Export singleton instance
export const responsivePositionTranslator = new ResponsivePositionTranslator();
